import React from 'react';
import { Link } from 'react-router-dom';
import './BridgeClubLegal.css';

const BridgeClubPrivacy: React.FC = () => {
  return (
    <div className="bridge-club-legal-page">
      <div className="legal-container">
        <div className="legal-header">
          <Link to="/bridge-club" className="back-link">
            &larr; Back
          </Link>
          <img src="/rbf-logo.png" alt="Red Bridge Farm Feed Co." className="legal-logo" />
        </div>

        <h1>Privacy Policy</h1>
        <p className="legal-effective">Effective Date: January 1, 2025</p>
        <p className="legal-version">Version 1.0</p>

        <div className="legal-content">
          <section>
            <h2>1. Introduction</h2>
            <p>
              Red Bridge Farm Feed Co. ("Red Bridge," "we," "us," or "our") respects your privacy
              and is committed to protecting your personal information. This Privacy Policy explains
              how we collect, use, disclose, and safeguard your information when you use the Bridge
              Club service.
            </p>
          </section>

          <section>
            <h2>2. Information We Collect</h2>

            <h3>2.1 Information You Provide</h3>
            <ul>
              <li><strong>Account Information:</strong> Name, email address, phone number, and password</li>
              <li><strong>Location Information:</strong> City, state, and ZIP code for weather alerts and store recommendations</li>
              <li><strong>Animal Information:</strong> Animal types, counts, breeds, weights, and feeding data</li>
              <li><strong>Purchase Information:</strong> Receipt images, purchase dates, products purchased, and amounts</li>
              <li><strong>Communications:</strong> Feedback, support requests, and other messages you send us</li>
            </ul>

            <h3>2.2 Information Collected Automatically</h3>
            <ul>
              <li><strong>Usage Data:</strong> Features used, pages visited, and actions taken in the app</li>
              <li><strong>Device Information:</strong> Device type, operating system, and browser type</li>
              <li><strong>Log Data:</strong> IP address, access times, and referring URLs</li>
            </ul>
          </section>

          <section>
            <h2>3. How We Use Your Information</h2>
            <p>We use the information we collect to:</p>
            <ul>
              <li>Provide and maintain the Bridge Club service</li>
              <li>Process your loyalty rewards and purchases</li>
              <li>Send you feed reminders and weather alerts</li>
              <li>Calculate personalized feeding recommendations</li>
              <li>Communicate with you about your account and our services</li>
              <li>Improve our products and services based on aggregate usage patterns</li>
              <li>Detect and prevent fraud and abuse</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2>4. Information Sharing</h2>
            <p>We may share your information in the following circumstances:</p>

            <h3>4.1 With Retail Partners</h3>
            <p>
              If you link your account to a retail partner store, we share limited information
              (name, member code, loyalty status) to enable reward redemption. We may share
              aggregate, anonymized purchasing trends with retail partners to help them stock
              appropriate inventory.
            </p>

            <h3>4.2 Service Providers</h3>
            <p>
              We work with third-party service providers who perform services on our behalf,
              such as email delivery, cloud hosting, and analytics. These providers have access
              to your information only as needed to perform their functions.
            </p>

            <h3>4.3 Legal Requirements</h3>
            <p>
              We may disclose your information if required by law or if we believe such action
              is necessary to comply with legal obligations, protect our rights, or ensure
              the safety of users.
            </p>

            <h3>4.4 Business Transfers</h3>
            <p>
              In the event of a merger, acquisition, or sale of assets, your information may
              be transferred as part of that transaction.
            </p>
          </section>

          <section>
            <h2>5. Data Security</h2>
            <p>
              We implement appropriate technical and organizational measures to protect your
              personal information against unauthorized access, alteration, disclosure, or
              destruction. These measures include encryption, secure server infrastructure,
              and access controls.
            </p>
            <p>
              However, no method of transmission over the internet is 100% secure. While we
              strive to protect your information, we cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2>6. Data Retention</h2>
            <p>
              We retain your personal information for as long as your account is active or as
              needed to provide services. We may retain certain information after account
              closure for legal compliance, fraud prevention, or legitimate business purposes.
            </p>
            <p>
              Purchase history and loyalty data are retained for tax and audit purposes
              for a minimum of 7 years.
            </p>
          </section>

          <section>
            <h2>7. Your Rights and Choices</h2>
            <p>You have the following rights regarding your personal information:</p>
            <ul>
              <li><strong>Access:</strong> Request a copy of the information we hold about you</li>
              <li><strong>Correction:</strong> Update or correct inaccurate information</li>
              <li><strong>Deletion:</strong> Request deletion of your account and personal data</li>
              <li><strong>Opt-Out:</strong> Unsubscribe from marketing emails and notifications</li>
              <li><strong>Data Portability:</strong> Request your data in a portable format</li>
            </ul>
            <p>
              To exercise these rights, contact us at privacy@theredbridgefarm.com or through
              the app's settings.
            </p>
          </section>

          <section>
            <h2>8. Cookies and Tracking</h2>
            <p>
              We use cookies and similar technologies to maintain your session, remember your
              preferences, and improve our services. You can control cookies through your
              browser settings, but disabling them may affect service functionality.
            </p>
          </section>

          <section>
            <h2>9. Children's Privacy</h2>
            <p>
              Bridge Club is not intended for children under 18. We do not knowingly collect
              personal information from children. If you believe a child has provided us with
              personal information, please contact us immediately.
            </p>
          </section>

          <section>
            <h2>10. Third-Party Links</h2>
            <p>
              Our service may contain links to third-party websites. We are not responsible
              for the privacy practices of these external sites. We encourage you to review
              their privacy policies.
            </p>
          </section>

          <section>
            <h2>11. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of
              significant changes via email or through the app. The "Effective Date" at the
              top of this policy indicates when it was last revised.
            </p>
          </section>

          <section>
            <h2>12. Contact Us</h2>
            <p>
              If you have questions about this Privacy Policy or our data practices, please contact us:
            </p>
            <address>
              Red Bridge Farm Feed Co.<br />
              Privacy Inquiries<br />
              145 W 3rd Ave, Kettle Falls, WA 99141<br />
              Email: privacy@theredbridgefarm.com<br />
              Phone: (509) 738-9004
            </address>
          </section>

          <section>
            <h2>13. California Privacy Rights</h2>
            <p>
              If you are a California resident, you may have additional rights under the
              California Consumer Privacy Act (CCPA), including the right to know what
              personal information we collect and the right to request deletion. To exercise
              these rights, contact us at privacy@theredbridgefarm.com.
            </p>
          </section>
        </div>

        <div className="legal-footer">
          <Link to="/bridge-club/terms" className="legal-link">
            Terms of Service
          </Link>
        </div>
      </div>
    </div>
  );
};

export default BridgeClubPrivacy;
